import { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "motion/react";
import { nav, profile } from "../data";

const SILK: [number, number, number, number] = [0.22, 1, 0.36, 1];

function MenuGlyph({ open }: { open: boolean }) {
  return (
    <span className="relative flex h-3 w-5 flex-col justify-between">
      <motion.span
        className="h-px w-full bg-current"
        animate={open ? { rotate: 45, y: 5.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.4, ease: SILK }}
      />
      <motion.span
        className="h-px w-full bg-current"
        animate={open ? { rotate: -45, y: -5.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.4, ease: SILK }}
      />
    </span>
  );
}

export function Header() {
  const { scrollY, scrollYProgress } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<string>("");
  const [menuOpen, setMenuOpen] = useState(false);

  useMotionValueEvent(scrollY, "change", (value) => {
    setScrolled(value > 28);
  });

  useEffect(() => {
    const sections = nav
      .map((item) => document.getElementById(item.href.slice(1)))
      .filter((el): el is HTMLElement => Boolean(el));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (query.matches) setMenuOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <motion.div
        aria-hidden="true"
        style={{ scaleX: scrollYProgress }}
        className="h-[2px] origin-left bg-gradient-to-r from-blush via-orchid to-azure"
      />
      <div
        className={`transition-colors duration-500 ${
          scrolled || menuOpen
            ? "border-b border-white/[0.07] bg-midnight/55 backdrop-blur-xl"
            : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-4 sm:px-8">
          <a href="#top" className="flex shrink-0 items-center gap-3" onClick={() => setMenuOpen(false)}>
            <span className="glass grid h-9 w-9 place-items-center rounded-[13px] text-display text-[0.8rem] font-semibold text-porcelain">
              {profile.monogram}
            </span>
            <span className="hidden text-display text-[0.98rem] text-porcelain sm:block">
              {profile.first} <span className="text-display-italic text-blush/90">{profile.last}</span>
            </span>
          </a>

          <nav
            aria-label="Sections"
            className="ml-auto hidden items-center gap-1 md:flex"
          >
            {nav.map((item) => {
              const isActive = active === item.href.slice(1);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative whitespace-nowrap rounded-full px-3 py-1.5 text-[0.82rem] transition-colors duration-300 ${
                    isActive ? "text-porcelain" : "text-mist hover:text-porcelain"
                  }`}
                >
                  {isActive ? (
                    <motion.span
                      layoutId="nav-active"
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      className="absolute inset-0 rounded-full bg-white/[0.08] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
                    />
                  ) : null}
                  <span className="relative">{item.label}</span>
                </a>
              );
            })}
          </nav>

          <a
            href={profile.resume}
            className="btn btn-ghost ml-auto hidden shrink-0 !px-4 !py-2 !text-[0.8rem] md:inline-flex"
          >
            Résumé
          </a>

          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((value) => !value)}
            className="glass ml-auto grid h-10 w-10 place-items-center rounded-[13px] text-porcelain md:hidden"
          >
            <MenuGlyph open={menuOpen} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen ? (
          <motion.div
            id="mobile-menu"
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: SILK }}
            className="fixed inset-0 -z-10 bg-midnight/80 backdrop-blur-2xl md:hidden"
          >
            <div className="flex h-full flex-col justify-center px-8 pt-24 pb-14">
              <nav aria-label="Sections" className="flex flex-col">
                {nav.map((item, index) => (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.55, delay: 0.06 + index * 0.055, ease: SILK }}
                    className="text-display border-b border-white/[0.08] py-3.5 text-[1.9rem] leading-tight text-porcelain"
                  >
                    {item.label}
                  </motion.a>
                ))}
              </nav>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4, ease: SILK }}
                className="mt-10 flex flex-col gap-3 text-[0.92rem] text-mist"
              >
                <a href={`mailto:${profile.email}`} className="link-underline w-fit">
                  {profile.email}
                </a>
                <a
                  href={profile.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline w-fit"
                >
                  {profile.linkedinLabel}
                </a>
                <a href={profile.resume} className="link-underline w-fit">
                  Download résumé
                </a>
              </motion.div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
